import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';

import { DetailPanel } from '@/components/detail-panel';
import { GarmentMockup } from '@/components/garment-mockup';
import { openRowProps } from '@/components/open-row';
import { usePagedList } from '@/lib/use-paged-list';
import { Pager } from '@/components/pager';
import { Badge } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { swatchFor } from '@/lib/swatch';
import { adminShellQuery, adminStockQuery } from '@/queries';
import { MatchingPanel, NewStockItem } from '@/components/admin/stock-matching';
import { useQuery } from '@tanstack/react-query';
import { buttonClass } from '@/components/ui';
import { DownloadCsv } from '@/components/admin/download-csv';
import type { StockRow } from '@/lib/stock-types';
import { CLASS_WORD, STOCK_CLASSES, classWord, type StockClass } from '@/lib/stock-class';
import type { GarmentType } from '@/lib/types';
import { StockItem } from './admin-stock-item';

/**
 * Stock. Spec 003 US-2, tasks T053 and T054.
 *
 * Two things this screen is careful about.
 *
 * **Free, not on hand.** The number that decides whether a job can start is
 * `free`, which is `on_hand` minus `reserved`. It is a view column, never
 * stored, so it cannot go stale. `free` leads; on hand and reserved sit beside
 * it for the people who need to see why.
 *
 * **No cost anywhere.** `items.avg_cost_cents` is not granted to a signed-in
 * caller at all, so it is not in the payload and cannot be shown by accident
 * (R-90). Cost lives on the job cost screen, behind the costs permission.
 *
 * **A page, not a slice.** This screen asked for 500 rows out of 1,057 and
 * printed "500 lines" in its header, so a third of the client's stock was not
 * on the page and nothing said so. Search, the kind filter and the low filter
 * now go to the SERVER with the page number, because a filter applied to the
 * rows already fetched searches the slice and calls it the shelf. R-231.
 *
 * The rows are still virtualised. Fifty rows do not need it, but the page size
 * is a number in a query string and the virtualiser costs nothing.
 */

/**
 * 32px, and it never grows.
 *
 * A checkbox, a pill and an avatar all fit inside 32, which is the sheet's
 * whole argument. It was 44, and in a 62vh scroller that costs between five and
 * eight rows depending on the monitor: a third of the screen. R-187.
 */
const ROW_HEIGHT = 32;

function tone(row: StockRow): 'ok' | 'warn' | 'bad' {
  if (Number(row.free) < 0) return 'bad';
  const point = row.items?.reorder_point;
  if (point != null && Number(row.free) <= Number(point)) return 'warn';
  return 'ok';
}

/**
 * What the line IS, at 24px, inside a 32px row.
 *
 * The stock list was a wall of codes. `DEMO-TEE-BLK-M` and `DEMO-TEE-BLK-L` are
 * one character apart and a person scanning a shelf is looking for a black tee,
 * not a string. The sheet allows this: "a checkbox, a pill and an avatar all
 * fit inside 32".
 *
 * A material has no variant by constraint, so it gets a tile with the first
 * letter of its class. Ink, film and thread are not garments and drawing one
 * for them would be a lie that looks like a fact. R-195.
 */
/**
 * How much of a shelf is spoken for. 1e of the design sheet.
 *
 * Staff lose Avg cost and Value, and the freed width goes to a bar rather than
 * being redistributed: grey reserved, ink free, against on hand. The row still
 * fills, and somebody who cannot see money can still see the shape of the
 * shelf, which is the part of the answer they need. R-215.
 *
 * The prop is `committed` because that is the name the totals contract uses
 * (`StockTotals.committed`). Every word a person reads says RESERVED. Guy, 19
 * October, tracker #10: "we do have Free, On Hand & Reserved". He read the item
 * panel, which says Reserved, and this list said Committed for the same number.
 * One number with two names is one number nobody can reconcile. R-414.
 */
function AvailabilityBar({
  onHand, committed, free,
}: { onHand: number; committed: number; free: number }) {
  // Nothing on the shelf is a real state and not a divide by zero.
  if (onHand <= 0) {
    return <span className="text-[11px] text-muted">nothing on hand</span>;
  }
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / onHand) * 100))}%`;

  return (
    <span
      className="flex h-1.5 w-full overflow-hidden rounded-[4px] bg-deep"
      title={`${committed} reserved, ${free} free, of ${onHand}`}
      aria-label={`${committed} reserved, ${free} free, of ${onHand}`}
    >
      <span className="bg-line-strong" style={{ width: pct(committed) }} />
      <span className="bg-[color:var(--color-ink)]" style={{ width: pct(free) }} />
    </span>
  );
}

/**
 * The one piece of colour on the screen.
 *
 * The sheet is explicit that a status is the only colour in the back office, so
 * each of the four has to earn it. The WORD is always there: status is never
 * carried by colour alone, which is the accessibility rule and also what makes
 * it readable in a photocopy.
 */
const STATUS_LABEL: Record<StockRow['status'], { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' }> = {
  in_stock: { label: 'In stock', tone: 'neutral' },
  low: { label: 'Low', tone: 'warn' },
  out: { label: 'Out of stock', tone: 'danger' },
  oversold: { label: 'Oversold', tone: 'danger' },
  counting: { label: 'Counting', tone: 'ok' },
};

function StatusPill({ status }: { status: StockRow['status'] }) {
  const s = STATUS_LABEL[status] ?? STATUS_LABEL.in_stock;
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

function StockThumb({ item }: { item: StockRow['items'] }) {
  const variant = item?.product_variants;

  /**
   * A REAL PHOTOGRAPH WHEN THERE IS ONE. Tracker #3.
   *
   * Three steps down, and the order matters. The colourway shot first, because
   * a stock line is a colour and a size and the product's first picture is
   * whatever colour happened to be photographed first. Then the product's own
   * first photograph. Then the drawn garment shape, which was the right answer
   * while the database held no pictures at all and is the wrong one now that
   * it holds 1,484.
   */
  const photo = variant?.image_url
    ?? [...(variant?.products?.product_images ?? [])]
      .sort((a, b) => a.sort - b.sort)[0]?.url;

  if (photo) {
    return (
      <span className="grid h-6 w-6 place-items-center overflow-hidden rounded-[4px] border border-line bg-raised">
        <img
          src={photo}
          alt=""
          loading="lazy"
          className="h-6 w-6 object-cover"
        />
      </span>
    );
  }

  if (!variant) {
    return (
      <span
        className="grid h-6 w-6 place-items-center rounded-[4px] border border-line bg-raised font-[family-name:var(--font-mono)] text-[10px] uppercase text-muted"
        title={item?.material_class ?? 'material'}
        aria-hidden
      >
        {(item?.material_class ?? '?').slice(0, 1)}
      </span>
    );
  }

  return (
    <span className="grid h-6 w-6 place-items-center overflow-hidden rounded-[4px] border border-line bg-raised">
      <GarmentMockup
        type={(variant.products?.type ?? 'other') as GarmentType}
        colorHex={swatchFor(variant.colour).swatch.hex}
        designUrl={null}
        placement={{ pos_x: 50, pos_y: 48, scale: 60 }}
        label={`${variant.colour} ${variant.products?.type ?? 'garment'}`}
        className="h-6 w-6"
      />
    </span>
  );
}

export default function AdminStockPage() {
  const navigate = useNavigate();
  /** The item open OVER the list. From the URL, so it is a link somebody can send. */
  const search = useSearch({ strict: false }) as { open?: string };
  const openId = search.open ?? null;
  /**
   * Which stock level this screen shows. Tracker #13, twice.
   *
   * It was a "Low stock only" checkbox, so the screen had no third state and
   * could only ever show lines that HAVE stock. A stock line is made when
   * stock arrives, so an item that has never had any had no line and was not
   * on the screen at all. The header counted the lines it had and said nothing
   * about the rest, which reads as "we have some of every line in stock".
   *
   * Three buttons fixed that and left a fourth hole, which Guy found the next
   * day: "I still cant get a complete list". Each button was one third of the
   * stock and there was no way to ask for all of it. So there is an All, and
   * the screen opens on it, because the complete list is the honest answer to
   * "what stock do we have" and the other three are narrowings of it.
   *
   * One selector rather than four checkboxes, because the levels are
   * exclusive: an item with none in stock has no line to be low.
   */
  const [shows, setShows] = useState<'all' | 'in' | 'low' | 'none'>('all');
  const lowOnly = shows === 'low';
  const noneOnly = shows === 'none';
  const everyLevel = shows === 'all';
  // T074 asked for a materials screen. This is that screen, reached by a filter
  // rather than duplicated: the columns, the virtualiser and the cost boundary
  // would all have been copied, and two copies drift.
  const [kind, setKind] = useState<'all' | 'material' | 'garment'>('all');
  /**
   * The class filter (spec 014, FR-1406) and Show archived (FR-1403). The
   * list opens without Archived items, because an archived item is history
   * and not stock. Choosing the Archived class shows them alone.
   */
  const [cls, setCls] = useState<'' | StockClass>('');
  const [archived, setArchived] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  // Spec 011. A new stock item from a product's own lists, and the spreadsheet
  // that gives each stock item its parent product.
  const [tool, setTool] = useState<'none' | 'new' | 'match'>('none');
  const can = useQuery(adminShellQuery).data?.can ?? {};
  const mayCreate = (can.catalogue ?? []).includes('create');
  const mayMatch = (can.catalogue ?? []).includes('edit');

  /**
   * The page, the debounce and the reset to page one. R-272.
   *
   * Typing does not fetch: a request per keystroke over a table this size is
   * how a search box becomes a load test, and the last reply to arrive is not
   * always the last one typed. Changing a filter goes back to page one, because
   * staying on page 7 of a search that now has two results shows an empty
   * table.
   *
   * `show_cost` still comes from the SERVER, which asked the permission matrix.
   * An `if` in the browser is a suggestion; the cost columns come back null
   * when the answer is no, so hiding them here is presentation, not a boundary.
   */
  const list = usePagedList<StockRow>(
    ({ page, q }) => adminStockQuery({
      page, q, kind, low: lowOnly, none: noneOnly, all: everyLevel, cls: cls || undefined, archived: archived || undefined,
    }) as never,
    [kind, lowOnly, noneOnly, everyLevel, cls, archived],
  );
  const { setPage, query, setQuery, needle, rows, isFetching } = list;
  const data = list.data as {
    show_cost?: boolean;
    totals?: {
      lines: number; locations: number; on_hand: number;
      committed: number; free: number; value_cents: number | null;
    };
  } | undefined;

  const showCost = data?.show_cost ?? false;
  const totals = data?.totals ?? {
    lines: 0, locations: 0, on_hand: 0, committed: 0, free: 0, value_cents: null,
  };

  const virtual = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  /**
   * The grid, once, so the header, every row and the footer cannot disagree.
   *
   * Three declarations of the same track list is how a table ends up with a
   * header one column out from its rows, and it is invisible until somebody
   * with the other permission opens it.
   */
  const columns = showCost
    ? '28px minmax(140px,1.4fr) 112px 96px 60px 64px 60px 60px 76px 84px 76px 100px'
    : '28px minmax(140px,1.4fr) 120px 1fr 64px 64px 64px 60px 80px 92px';

  // Counted on THIS PAGE, and said so. The whole-table count of short lines is
  // the reorder screen's job and it has one.
  const negative = rows.filter((r) => Number(r.free) < 0).length;

  return (
    <div>
      <div className="mb-[14px]">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Stock on hand</h1>
        {/* The sheet's subtitle: a count, the places it is in, and the money it
            is worth. Staff lose the money and keep the rest, rather than losing
            the line. R-215. */}
        <p className="mt-1 text-[12.5px] text-muted">
          {/* The whole shelf, not the page. These three numbers come from the
              database over every matching row, which is the only way a header
              can honestly say how much stock there is. */}
          {totals.lines.toLocaleString('en-AU')}{' '}
          {noneOnly
            ? `${totals.lines === 1 ? 'item' : 'items'} with none in stock`
            : everyLevel
              /* The all-levels count is of ITEMS, because an item on no shelf
                 has no line to count. Tracker #13. */
              ? (totals.lines === 1 ? 'item' : 'items')
              : (totals.lines === 1 ? 'line' : 'lines')}
          {/* A count of shelves and a value are both zero for a list of items
              that are on no shelf, so neither is said. Tracker #13. */}
          {!noneOnly && (
            <>
              {' · '}
              {totals.locations} {totals.locations === 1 ? 'location' : 'locations'}
              {showCost && totals.value_cents != null && (
                <>
                  {' · '}
                  <span className="font-[family-name:var(--font-mono)] tabular-nums text-ink">
                    {formatCents(totals.value_cents)}
                  </span>
                  {' at cost'}
                </>
              )}
            </>
          )}
        </p>
      </div>

      {(mayCreate || mayMatch) && (
        <div className="mb-4 flex flex-wrap gap-2">
          {mayCreate && (
            <button
              type="button"
              aria-pressed={tool === 'new'}
              className={buttonClass(tool === 'new' ? 'primary' : 'secondary', 'sm')}
              onClick={() => setTool(tool === 'new' ? 'none' : 'new')}
            >
              New stock item
            </button>
          )}
          {mayMatch && (
            <button
              type="button"
              aria-pressed={tool === 'match'}
              className={buttonClass(tool === 'match' ? 'primary' : 'secondary', 'sm')}
              onClick={() => setTool(tool === 'match' ? 'none' : 'match')}
            >
              Match stock to products
            </button>
          )}
        </div>
      )}
      {tool === 'new' && (
        <NewStockItem
          onClose={() => setTool('none')}
          onOpen={(id) => {
            setTool('none');
            void navigate({ to: '/admin/stock', search: { open: id } as never });
          }}
        />
      )}
      {tool === 'match' && <MatchingPanel onClose={() => setTool('none')} />}

      {negative > 0 && (
        <div className="mb-4 rounded-[12px] border border-line bg-panel p-4 text-[14px]">
          <strong>{negative} {negative === 1 ? 'line on this page is' : 'lines on this page are'} promised
          beyond what exists.</strong>{' '}
          Free has gone negative, which means stock was written off or miscounted after
          it was reserved. Somebody needs to ring those customers.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a code or a name"
          aria-label="Search stock"
          className="h-10 min-w-[280px] flex-1 rounded-[10px] border border-line bg-panel px-3 text-[14px]"
        />
        <div className="flex items-center gap-1">
          {(['all', 'material', 'garment'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`rounded-[10px] border px-3 py-2 text-[14px] ${
                kind === k ? 'border-line-strong font-semibold' : 'border-line text-muted'
              }`}
            >
              {k === 'all' ? 'Everything' : k === 'material' ? 'Materials' : 'Garments'}
            </button>
          ))}
        </div>

        {/* Tracker #13. "None in stock" is the list that was never reachable:
            an item gets a stock line when stock arrives, so one that has never
            had any had no line to show. "All levels" is the whole shelf and
            everything off it, which is what the screen opens on. The four are
            exclusive, and the server refuses two of them together rather than
            dropping one. */}
        <div className="flex items-center gap-1">
          {(['all', 'in', 'low', 'none'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={shows === s}
              onClick={() => setShows(s)}
              className={`rounded-[10px] border px-3 py-2 text-[14px] ${
                shows === s ? 'border-line-strong font-semibold' : 'border-line text-muted'
              }`}
            >
              {s === 'all' ? 'All levels'
                : s === 'in' ? 'In stock'
                  : s === 'low' ? 'Low stock' : 'None in stock'}
            </button>
          ))}
        </div>
        <select
          aria-label="Class"
          value={cls}
          onChange={(e) => setCls(e.target.value as '' | StockClass)}
          className="h-10 rounded-[10px] border border-line bg-panel px-2 text-[14px]"
        >
          <option value="">Every class</option>
          {STOCK_CLASSES.map((c) => <option key={c} value={c}>{CLASS_WORD[c]}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-[14px]">
          <input
            type="checkbox"
            checked={archived || cls === 'archived'}
            disabled={cls !== ''}
            onChange={(e) => setArchived(e.target.checked)}
          />
          Show archived
        </label>
        {isFetching && <span className="text-[13px] text-muted">Loading...</span>}
        <DownloadCsv
          path="/api/stock/export"
          params={{
            q: needle.trim(),
            kind: kind === 'all' ? undefined : kind,
            class: cls || undefined,
            archived: archived ? '1' : undefined,
            low: lowOnly ? '1' : undefined,
            none: noneOnly ? '1' : undefined,
            all: everyLevel ? '1' : undefined,
          }}
        />
      </div>

      {rows.length === 0 && (needle || lowOnly || noneOnly || kind !== 'all' || cls !== '') ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center shadow-[var(--shadow-1)]">
          <p className="text-[16px] font-semibold">Nothing matches that.</p>
          <p className="mx-auto mt-2 max-w-[46ch] text-[13px] text-muted">
            The search covers the item code and the item name, across every
            line, not only the ones on screen. Clear the filters to see the
            whole shelf again.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center shadow-[var(--shadow-1)]">
          <p className="text-[16px] font-semibold">No stock yet.</p>
          <p className="mx-auto mt-2 max-w-[46ch] text-[13px] text-muted">
            Items appear here once the catalogue is imported and the first goods
            receipt is entered. Stock cannot be typed in directly: it arrives
            through a receipt so that every unit carries a cost and a purchase
            order behind it.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border border-line bg-panel shadow-[var(--shadow-1)]">
          {/* 34px, sticky, on the inset surface, 10px mono uppercase. This and
              the column heads are one of the two places uppercase survives. */}
          <div
            className="sticky top-0 z-10 grid h-[34px] items-center gap-3 border-b border-line
                       bg-raised px-[14px] font-[family-name:var(--font-mono)] text-[10px]
                       font-medium uppercase tracking-[0.08em] text-muted"
            style={{ gridTemplateColumns: columns }}
          >
            <span />
            <span>Item</span>
            <span>SKU</span>
            {showCost ? <span>Location</span> : <span>Availability</span>}
            <span className="text-right">On hand</span>
            <span className="text-right">Reserved</span>
            <span className="text-right">Free</span>
            <span className="text-right">Reorder</span>
            {showCost && <span className="text-right">Avg cost</span>}
            {showCost && <span className="text-right">Value</span>}
            <span>Class</span>
            <span>Status</span>
          </div>

          <div ref={scroller} className="max-h-[62vh] overflow-auto">
            <div style={{ height: virtual.getTotalSize(), position: 'relative' }}>
              {virtual.getVirtualItems().map((v) => {
                const row = rows[v.index]!;
                const t = tone(row);
                return (
                  <div
                    key={`${row.item_id}-${row.location_id}`}
                    // The whole row opens the item, the same gesture as every
                    // other list. This one is a grid of divs rather than a
                    // table, because it is virtualised, so it takes the props
                    // instead of the component. R-270.
                    {...openRowProps({
                      // `?open=`, so the list stays behind the panel and keeps
                      // its scroll position in a virtualised list of 1,057.
                      onOpen: () => void navigate({
                        to: '/admin/stock',
                        search: (old) => ({ ...old, open: row.item_id }),
                      }),
                      label: `Open ${row.items?.name ?? 'item'}`,
                    })}
                    className="absolute left-0 grid w-full cursor-pointer items-center gap-3
                               border-b border-line-soft px-[14px] text-[12.5px]
                               hover:bg-raised"
                    style={{
                      height: ROW_HEIGHT,
                      transform: `translateY(${v.start}px)`,
                      gridTemplateColumns: columns,
                    }}
                  >
                    <StockThumb item={row.items} />
                    <Link
                      to="/admin/stock"
                      search={(old) => ({ ...old, open: row.item_id })}
                      className="truncate hover:underline"
                    >
                      {row.items?.name}
                    </Link>
                    <span className="truncate font-[family-name:var(--font-mono)] text-[12px] text-muted">
                      {row.items?.code}
                    </span>

                    {showCost ? (
                      <span className="truncate text-muted">{row.locations?.code}</span>
                    ) : (
                      /* 1e: staff lose Avg cost, Value and Export, and the freed
                         width goes to an availability bar. Columns are REMOVED,
                         not rearranged: the grid keeps its order so the same
                         screen reads the same way for both people. R-215. */
                      <AvailabilityBar
                        onHand={Number(row.on_hand)}
                        committed={Number(row.reserved)}
                        free={Number(row.free)}
                      />
                    )}

                    <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
                      {Number(row.on_hand)}
                    </span>
                    {/* Reserved greys back because it is DERIVED. */}
                    <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
                      {Number(row.reserved)}
                    </span>
                    {/* Free is the only bold number in the row, because it is
                        the number a decision is made on. */}
                    <span className="text-right font-[family-name:var(--font-mono)] font-medium tabular-nums">
                      {Number(row.free)}
                    </span>
                    <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
                      {row.items?.reorder_point ?? ''}
                    </span>

                    {showCost && (
                      <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
                        {row.avg_cost_cents == null ? '' : formatCents(row.avg_cost_cents)}
                      </span>
                    )}
                    {showCost && (
                      <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
                        {row.value_cents == null ? '' : formatCents(row.value_cents)}
                      </span>
                    )}

                    {/* Spec 014, FR-1406. Archived carries a mark, because the
                        list shows it only when somebody asked to see it. */}
                    <span className="truncate text-muted">
                      {row.items?.line_status === 'archived'
                        ? <Badge tone="warn">Archived</Badge>
                        : classWord(row.items?.line_status)}
                    </span>
                    <span><StatusPill status={row.status} /></span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 36px footer, carrying the totals for EVERY matching line, not for
              the rows on screen. It says so, because a reader who cannot tell
              which of the two a number is cannot use either. No floating bar
              over the rows: a bar covers the thing somebody is reading. */}
          <div
            className="grid h-9 items-center gap-3 border-t border-line bg-raised px-[14px]
                       text-[12px] font-medium"
            style={{ gridTemplateColumns: columns }}
          >
            <span />
            <span className="text-muted">
              All {totals.lines.toLocaleString('en-AU')} matching{' '}
              {everyLevel || noneOnly ? 'items' : 'lines'}
            </span>
            <span />
            <span />
            <span className="text-right font-[family-name:var(--font-mono)] tabular-nums">
              {totals.on_hand.toLocaleString('en-AU')}
            </span>
            <span className="text-right font-[family-name:var(--font-mono)] tabular-nums text-muted">
              {totals.committed.toLocaleString('en-AU')}
            </span>
            <span className="text-right font-[family-name:var(--font-mono)] tabular-nums">
              {totals.free.toLocaleString('en-AU')}
            </span>
            <span />
            {showCost && <span />}
            {showCost && (
              <span className="text-right font-[family-name:var(--font-mono)] tabular-nums">
                {totals.value_cents == null ? '' : formatCents(totals.value_cents)}
              </span>
            )}
            <span />
            <span />
          </div>

          <Pager
            {...list.pager}
            // The all-levels and none lists are paged over items, not over
            // stock lines, so the pager counts what it is actually turning.
            noun={everyLevel || noneOnly ? 'item' : 'line'}
            onPage={(n) => {
              setPage(n);
              scroller.current?.scrollTo({ top: 0 });
            }}
          />
        </div>
      )}

      <DetailPanel
        open={openId != null}
        onClose={() => void navigate({
          to: '/admin/stock',
          search: (old) => ({ ...old, open: undefined }),
        })}
        label="Stock item"
        width="wide"
      >
        {openId && (
          <Suspense fallback={<div className="p-5 text-[13px] text-muted">One moment.</div>}>
            <StockItem key={openId} itemId={openId} embedded />
          </Suspense>
        )}
      </DetailPanel>
    </div>
  );
}
